import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Col, Image, Row, Container } from "react-bootstrap";
import "./About.css";

const fade = (dir = "left", delay = 0) => ({
  hidden: { opacity: 0, x: dir === "left" ? -16 : 16, y: 8 },
  visible: {
    opacity: 1, x: 0, y: 0,
    transition: { duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }
  }
});

const About = () => {
  const [showMore, setShowMore] = useState(false);

  const primary = useMemo(() => ([
    {
      title: "Le type d’entreprise à choisir",
      img: "/images/about/type.webp",
      alt: "Type d’entreprise",
      copy: `Avant de se lancer dans la création d’entreprise, il faut maîtriser certains prérequis. En fonction du capital, de la localisation spatiale, de l’activité exercée, de la forme juridique et de la typologie, nous mettons à votre disposition des renseignements suffisants pour vous aider à bien démarrer.`,
      imageLeft: true,
    },
    {
      title: "Domiciliation",
      img: "/images/about/domiciliation.webp",
      alt: "Domiciliation",
      copy: `La domiciliation est définie comme la fixation d’une adresse à un lieu. Nous vous offrons la possibilité de domicilier votre entreprise chez nous afin de bénéficier de nombreux services comme la réception, le scan, le tri et la redirection de vos courriers, la localisation fiscale, ainsi que d’une adresse administrative.`,
      imageLeft: false,
    },
    {
      title: "Gestion de votre entreprise",
      img: "/images/about/gestion.webp",
      alt: "Gestion",
      copy: `Après sa création, une entreprise doit être gérée au quotidien. Dans cette optique, nous vous proposons d’assurer le montage de vos devis, factures et bons de commande. Chaque document sera transmis au client pour validation, avec un système de signature électronique sécurisée et une traçabilité complète de toutes les actions réalisées, garantissant ainsi transparence et fiabilité à chaque étape.`,
      imageLeft: true,
    },
    {
      title: "Gestion des ressources humaines",
      img: "/images/about/grh.webp",
      alt: "GRH",
      copy: `C’est un des volets les plus complexes qui prend en compte la paie, les contrats de travail ainsi que l’établissement et la rupture des contrats. La GRH s’assure également de l’assainissement du climat de social.`,
      imageLeft: false,
    },
  ]), []);

  const extra = useMemo(() => ([
    {
      title: "Rattachement fiscal",
      img: "/images/about/fiscal.webp",
      alt: "Rattachement fiscal",
      copy: `C’est une procédure qui consiste à lier une entreprise à son centre d’impôts. Le rattachement se fait dans le centre le plus proche en fonction de la taille et du chiffre d’affaire.`,
      imageLeft: true,
    },
    {
      title: "Gestion de la relation client",
      img: "/images/about/relation.webp",
      alt: "Relation client",
      copy: `L’objectif principal de toute entreprise est de générer du profit. Pour y parvenir, il est essentiel de maintenir une relation de qualité avec ses clients grâce à un suivi efficace. Il est également crucial d’effectuer des relances régulières afin de préserver un contact permanent.`,
      imageLeft: false,
    },
    {
      title: "Documents indispensables pour le créateur d’Entreprise",
      img: "/images/about/docs.webp",
      alt: "Documents indispensables",
      copy: `Pour lancer votre entreprise, il est essentiel de vous munir de certains documents spécifiques, notamment :`,
      list: [
        "Votre pièce d’identité (Passeport ou carte de séjour pour les étrangers)",
        "Extrait du casier judiciaire",
        "Acte de mariage si vous êtes marié(e)",
        "Liste conforme des gérants, administrateurs, dirigeants ou associés",
        "Pièce d’identité du ou des co-gérants (Passeport, carte de séjour pour les étrangers, ou acte de naissance pour les mineurs)",
        "Plan de localisation de l'entreprise",
      ],
      imageLeft: true,
    },
    {
      title: "Rédaction des statuts",
      img: "/images/about/statuts.webp",
      alt: "Rédaction des statuts",
      copy: `Les statuts représentent la carte d’identité de l’entreprise, contenant ses informations essentielles : nom, objet social, capital social, forme juridique, ainsi que la répartition des actions/parts.`,
      imageLeft: false,
    },
    {
      title: "Administration de votre entreprise",
      img: "/images/about/administration.webp",
      alt: "Administration",
      copy: `Administrer une entreprise revient à suivre sa comptabilité, ses cotisations sociales, ses Déclarations Statistiques et Fiscales (DSF) ainsi que ses télédéclarations mensuelles.`,
      imageLeft: true,
    },
  ]), []);

  const Block = ({ section, index }) => {
    const left = section.imageLeft;
    return (
      <article className="about-block">
        <Row className={`align-items-center gy-4 ${left ? "" : "flex-row-reverse"}`}>
          {/* Image — sans bordure, taille maîtrisée */}
          <Col xs={12} lg={5}>
            <motion.div
              className="about-media"
              variants={fade(left ? "left" : "right", 0.05)}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <Image
                src={section.img}
                alt={section.alt}
                loading="lazy"
                className="about-img"
                fluid
              />
            </motion.div>
          </Col>

          {/* Texte — ALIGNÉ À GAUCHE */}
          <Col xs={12} lg={7}>
            <motion.div
              className="about-text"
              variants={fade(left ? "right" : "left", 0.08)}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <h3 className="about-h3">
                <span className="about-bullet">{index + 1}</span>
                {section.title}
              </h3>
              <p className="about-copy">{section.copy}</p>
              {section.list && (
                <ul className="about-list">
                  {section.list.map((li, i) => <li key={i}>{li}</li>)}
                </ul>
              )}
            </motion.div>
          </Col>
        </Row>

        {/* Divider discret entre blocs */}
        <div className="about-divider" />
      </article>
    );
  };

  return (
    <section id="about" className="about-wrap" aria-labelledby="about-title">
      <Container>
        <header className="about-header">
          <p className="eyebrow">Avant de vous lancer</p>
          <h2 id="about-title" className="about-title-lg">
            L’entreprise est une grande <span className="accent">AVENTURE</span>…
            <span className="about-subtitle">Ce que vous devez savoir avant de vous lancer</span>
          </h2>
        </header>

        {primary.map((s, i) => (
          <Block key={`p-${i}`} section={s} index={i} />
        ))}

        {showMore && extra.map((s, i) => (
          <Block key={`e-${i}`} section={s} index={primary.length + i} />
        ))}

        <div className="about-cta">
          <button
            type="button"
            className="about-toggle"
            onClick={() => setShowMore(v => !v)}
            aria-expanded={showMore}
          >
            {showMore ? "Voir moins" : "Voir plus"}
          </button>
        </div>
      </Container>
    </section>
  );
};

export default About;
